// Core
import { Box, Stack, Radio, Flex, Textarea, Image, Text, useColorModeValue } from '@chakra-ui/react';

// Assets
import emojiImage from 'assets/img/red-emoji.png';
import emojiLiImage from 'assets/img/red-emoji-2.png';
import coinImage from 'assets/img/red-coin.png';

// Components
import Card from 'components/Card/Card';

const ContinueSavingLi = ({ text }) => {
	const textColor = useColorModeValue('#0E1633', '#E1E1E1');

    return (
        <Flex gap="12px" alignItems="center" mb="8px">
            <Image src={emojiLiImage} w="32px" h="32px" />
            <Text color={textColor} dangerouslySetInnerHTML={{ __html: text }} />
        </Flex>
    );
};

const ContinueSaving = ({ 
	userData, setStep,
	reasonRadio, setReasonRadio,
	reasonRadioOther, setReasonOtherRadio,
	changeStatusSubItem,
	keepsAnotherPlan
}) => {
	const galleryShadow = useColorModeValue('#E0EAFF', '#00071C');
	const borderTextarea = useColorModeValue('1px solid transparent', '1px solid #262D47 !important');
	const borderReasonCard = useColorModeValue('1px solid transparent !important', '1px solid #262D47 !important');

	const entriesCount = userData?.entries ?? 0;
	// Entries and coins are account-wide balances, cancellation is per subscription.
	// Only claim they will be lost when this is the member's last live plan.
	const isLastActivePlan = keepsAnotherPlan === false;

	const handleReasonChange = (value) => {
	    setReasonRadio(value);
	};

	const handleReasonOtherChange = (value) => {
	    setReasonOtherRadio(value);
	};

    return (
    	<>
	        <Box>
		        <Text textAlign="center" variant="header" fontSize={{base: '36px', md: '44px', lg: '56px'}}>
		          Sorry To See You Leave
		        </Text>

		        <Text 
		        	textAlign="center" 
		        	maxW="520px" 
		        	m={{ base: '10px auto 24px auto', md: '16px auto 32px auto', lg: '20px auto 42px auto'}} 
		        	variant="header" 
		        	position="relative"
		        	zIndex="1"
		        	fontSize="24px">
		          Before you go, we`d like to know why you`re canceling this subscription.
		        </Text>
	        </Box>
	        
	        <Flex 
	        	borderRadius="16px" 
	        	gap="8px" p="4px 4px 4px 4px" 
	        	maxW="444px" w="100%" 
	        	textAlign="center" 
	        	m="0 auto"
	        	bg="white" 
	        	zIndex="5"
	        	position="relative"
	        	justifyContent="center">
		      <Text 
		        fontSize={{ base: '20px', md: '24px'}}
		        fontStyle="italic"
		        fontWeight="700"
		        lineHeight="100%"
		        pr="4px"
		        sx={{
		          background: 'linear-gradient(180deg, #FD2A2A 0%, #AF0000 100%)',
		          backgroundClip: 'text',
		          '-webkit-background-clip': 'text',
		          '-webkit-text-fill-color': 'transparent'
		        }}
		        letterSpacing="-1px"
		        textTransform="uppercase"
		        >Are You Sure You Want to Leave?</Text>
		    </Flex>

		    <Box position="relative">
			    <Card zIndex="3" padding="20px 16px 12px 16px" maxW="484px" w="100%" m="16px auto" borderColor="#FF3B30" overflow="hidden" >
			    	<ContinueSavingLi text='You will <span class="red-text-underline">no longer be eligible</span> for upcoming giveaways' />
			    	{ isLastActivePlan &&
				    	<ContinueSavingLi text={`By cancelling this membership you’ll <span class="red-text-underline">lose</span> <span class="blue-text">${entriesCount}</span> ${entriesCount === 1 ? 'entry' : 'entries'}`} />
				    }
			    	{ isLastActivePlan &&
				    	<ContinueSavingLi text='Your LMCT+ Coins balance will be <span class="red-text-underline">frozen</span> and cannot be used' />
				    }
			    	<ContinueSavingLi text='You won’t be eligible for <span class="red-text-underline">partner discounts</span> without a subscription' />
				</Card>

		        <Box position="absolute" bottom="0" w="100%">
		        	<Image overflow="visible" zIndex="0" src={emojiImage} position="absolute" alt="Emoji" objectFit="cover" 
		        		w={{ base: '200px', sm: '300px', md: '390px'}} 
		        		h={{ base: '200px', md: '300px'}} 
		        		bottom={{ base: '88px', md: '-2px' }} 
		        		left={{ base: '-20%', md: '0%', lg: '83px'}} />
		        	<Image overflow="visible" zIndex="0" src={coinImage} position="absolute" alt="Coin"  objectFit="cover" 
		        		w={{ base: '200px', sm: '270px', md: '360px'}} 
		        		h={{ base: '200px', md: '300px'}}
		        		bottom="-70px" 
		        		right={{ base: '-20%', md: '0%', lg: '55px'}} />
		        	<Image overflow="visible" zIndex="0" src={coinImage} position="absolute" alt="Coin" objectFit="cover" 
		        		w={{ base: '180px', md: '240px'}} 
		        		h={{ base: '100px', md: '140px'}}
		        		bottom="-57px" 
		        		right={{ base: '-5%', md: '15%', lg:'170px'}}
		        		transform="rotate(30deg)" />
		        </Box>

		        <Box bg={galleryShadow} 
		          w={{ base: '150%', md: '100%'}}
		          h="340px" 
		          position="absolute" 
		          filter="blur(70px)" 
		          transform={{base: 'rotate(20deg)', md: 'unset'}}
		          left={{base: '-25%', md: 'unset'}}
		          zIndex="2"
		          bottom="-200px"
		          borderRadius="200px 200px 0 0" 
		          />
			</Box>

			<Box mt="64px" zIndex="3" position="relative">
		        <Text textAlign="center" variant="header" fontSize={{base: '36px', md: '44px', lg: '56px'}}>
		          Reason For Cancelling
		        </Text>

		        <Text textAlign="center" maxW="520px" 
		          m={{ base: '10px auto 16px auto', md: '16px auto 32px auto'}} 
		          variant="header" fontSize="24px">
		          Mind sharing why you’d like to cancel this subscription?
		        </Text>
	        </Box>

	        <Box maxW="614px" m="0 auto">
	        	<Stack>
	                {[
	                  { value: 'Not enough features' },
	                  { value: 'Unsatisfactory customer service' },
	                  { value: 'Not enough value' },
	                  { value: 'Too pricey' },
	                  { value: 'Difficult to use' },
	                  { value: 'Just taking a break - I`ll be back' },
	                  { value: 'Other' }
	                ].map(({ value }) => (
	                	<Card
	                		cursor="pointer" 
	                		border={ reasonRadio === value ? '1px solid #2ABDFD' : borderReasonCard }
	                		onClick={() => handleReasonChange(value)}
	                		padding="24px 12px" key={value} borderRadius="8px" mt="12px !important">
		                  <Radio
		                  	mb="-10px"
		                    value={value}
		                    isChecked={reasonRadio === value}
		                    pointerEvents="none"
		                  >
		                    <Text variant="text-md" onClick={() => handleReasonChange(value)} ml="-15px" pl="12px">{value}</Text>
		                  </Radio>
		                </Card>
	                ))}
	            </Stack>

	            { reasonRadio === 'Other' &&
	                <Box mt="24px">
	                    <Textarea
	                    	border={borderTextarea}
	                    	_active={{
	                    		border: borderTextarea
	                    	}}
	                    	_focus={{
	                    		border: borderTextarea
	                    	}}
	                    	value={reasonRadioOther ?? ''}
	                    	onChange={(event) => handleReasonOtherChange(event.target.value)}
	                    	h="163px !important"
	                    	resize="none"
	                    	maxLength={2000}
	                    	placeholder="Describe your reason (optional)"
	                    	></Textarea>
	                </Box>
	            }
            </Box>
	    </>
    );
};


export default ContinueSaving;